import MUIBox from '@/components/MUI-Components/MUI-Box';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import React, {Fragment, memo} from 'react';
import DisplayImage from '../display-image';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import parse from 'html-react-parser';
import {borderRadius, borderWidth, colors, fontFamily, fontSize, fontWeight, spacing, zIndex} from '@/styles/tokens';
import {nftButtonSx} from '@/styles/nftButtonSx';

// ─── Migrated from `_android.scss` `.process-grid-parent` (justify-content repeats the
// same value at every breakpoint in the source; kept per-breakpoint for fidelity) ──────
const processGridParentSx = {
    justifyContent: 'center !important',
    '@media (max-width: 1499px)': {justifyContent: 'center'},
    '@media (max-width: 1024px)': {justifyContent: 'center'},
    '@media (max-width: 768px)': {justifyContent: 'center'},
    '@media (max-width: 430px)': {justifyContent: 'center'}
} as const;

// ─── Migrated from `_android.scss` `.android-wrapper` base box. 1441px is a one-off
// breakpoint with no token match; 1024/768/430 map to laptopScreen/tabScreen/mobileScreen ─
const androidWrapperSx = {
    border: `${borderWidth.thin} dashed ${colors.text.inverse}`,
    paddingTop: '25px', // no exact spacing token (24=24px, 25px doesn't map)
    maxWidth: '440px !important',
    position: 'relative',
    background: colors.primary.main,
    borderRadius: borderRadius['10'],
    width: '100%',
    float: 'left',
    '@media (max-width: 1441px)': {maxWidth: '320px !important'},
    '@media (max-width: 1024px)': {
        maxWidth: '292px !important',
        '& .android-heading': {maxWidth: '250px !important'}
    },
    '@media (max-width: 768px)': {
        maxWidth: '335px !important',
        '& .android-heading': {maxWidth: '290px !important'}
    },
    '@media (max-width: 430px)': {maxWidth: '380px !important'}
} as const;

const androidHeadingSx = {
    backgroundColor: '#7e9fe4', // no token match
    maxWidth: '300px',
    display: 'flex',
    alignItems: 'center',
    '& p': {paddingLeft: '15px'} // no exact spacing token (16=16px, 15px doesn't map)
} as const;

const androidIconSx = {
    padding: spacing[20],
    // scoped override of the shared global `.step-image` (base filter:#000000 stays global —
    // 7 other components rely on it); only inside Process is it inverted to white.
    '& .step-image': {filter: 'brightness(0) invert(1)'}
} as const;

const androidDiscriptionSx = {
    padding: '0 15px 25px 15px', // no exact spacing tokens (15/25px don't map)
    // scoped override of the shared global `.description` (304 other files use it unstyled here)
    '& .description a': {color: `${colors.text.inverse} !important`, textDecoration: 'none !important'}
} as const;

const androidNumberSx = {
    fontSize: fontSize[42],
    fontFamily: `${fontFamily.heading} !important`,
    color: `${colors.text.inverse} !important`
} as const;

const androidHeadingInnerSx = {
    fontFamily: `${fontFamily.heading} !important`,
    fontWeight: `${fontWeight.bold} !important`,
    fontSize: `${fontSize[18]} !important`,
    '& a': {textDecoration: 'none !important', color: `${colors.text.inverse} !important`}
} as const;

// ─── Connector pseudo-elements — migrated from `_android.scss` `.android-wrapper::after` /
// `-2/-3/-4/-5`, `.process-wrapper-*::after` and their ipadScreen(992)/tabScreen(768)/
// mobileScreen(430) overrides. Every rendered item always carries the bare `android-wrapper`
// class (see `className` below), so `.android-wrapper::after` is the universal fallback
// connector for any index without its own numbered rule. ────────────────────────────────
const connectorBaseAfterSx = {
    content: '""',
    position: 'absolute',
    top: '50%',
    right: '-16px',
    zIndex: zIndex.raised,
    height: '30px',
    width: '30px',
    marginTop: '-15px',
    background: colors.primary.main,
    transform: 'rotate(-135deg)',
    msTransform: 'rotate(-135deg)',
    WebkitTransform: 'rotate(-135deg)',
    borderLeft: `${borderWidth.thin} dashed ${colors.text.inverse}`,
    borderBottom: `${borderWidth.thin} dashed ${colors.text.inverse}`
} as const;

// unconditional (desktop) patches onto connectorBaseAfterSx for specific indices
const bottomPatchSx = {bottom: '-16px', top: 'auto', left: 0, right: 0, margin: '0 auto', transform: 'rotate(-45deg)'} as const;
const leftNearPatchSx = {top: '50%', left: '-16px', transform: 'rotate(-135deg)', WebkitTransform: 'rotate(45deg)'} as const;

// responsive deltas — pure, verbatim-transcribed overrides; unspecified properties fall
// through from whichever unconditional rule applies, exactly like the original cascade
const ipadBottomDeltaSx = {
    content: '""',
    bottom: '-16px',
    top: 'auto',
    left: 0,
    right: 0,
    margin: '0 auto',
    transform: 'rotate(-45deg)'
} as const;
const ipadLeftFarDeltaSx = {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '-295px',
    zIndex: zIndex.raised,
    height: '30px',
    width: '30px',
    marginTop: '-15px',
    background: colors.primary.main,
    transform: 'rotate(-135deg)',
    msTransform: 'rotate(-135deg)',
    WebkitTransform: 'rotate(45deg)',
    borderLeft: `${borderWidth.thin} dashed ${colors.text.inverse}`,
    borderBottom: `${borderWidth.thin} dashed ${colors.text.inverse}`
} as const;
const ipadRightOffset275DeltaSx = {
    content: '""',
    position: 'absolute',
    top: '50%',
    right: '-16px',
    left: '275px',
    zIndex: zIndex.raised,
    height: '30px',
    width: '30px',
    marginTop: '-15px',
    background: colors.primary.main,
    transform: 'rotate(-135deg)',
    msTransform: 'rotate(-135deg)',
    WebkitTransform: 'rotate(-135deg)',
    borderLeft: `${borderWidth.thin} dashed ${colors.text.inverse}`,
    borderBottom: `${borderWidth.thin} dashed ${colors.text.inverse}`
} as const;
const ipadLeftNearDeltaSx = {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '-16px',
    zIndex: zIndex.raised,
    height: '30px',
    width: '30px',
    marginTop: '-15px',
    background: colors.primary.main,
    transform: 'rotate(-135deg)',
    msTransform: 'rotate(-135deg)',
    WebkitTransform: 'rotate(45deg)',
    borderLeft: `${borderWidth.thin} dashed ${colors.text.inverse}`,
    borderBottom: `${borderWidth.thin} dashed ${colors.text.inverse}`
} as const;
const tabHybridDeltaSx = {
    content: '""',
    bottom: '-16px',
    top: '50%',
    left: '-335px',
    right: 0,
    margin: '0 auto',
    transform: 'rotate(45deg)'
} as const;
const tabRightOffset319DeltaSx = {
    content: '""',
    position: 'absolute',
    top: '50%',
    right: '-16px',
    left: '319px',
    zIndex: zIndex.raised,
    height: '30px',
    width: '30px',
    marginTop: '-15px',
    background: colors.primary.main,
    transform: 'rotate(-135deg)',
    msTransform: 'rotate(-135deg)',
    WebkitTransform: 'rotate(-135deg)',
    borderLeft: `${borderWidth.thin} dashed ${colors.text.inverse}`,
    borderBottom: `${borderWidth.thin} dashed ${colors.text.inverse}`
} as const;
const mobileBottomDeltaSx = {
    content: '""',
    bottom: '-16px',
    top: 'auto',
    left: 0,
    right: 0,
    margin: '0 auto',
    transform: 'rotate(-45deg)'
} as const;

const HIDDEN_AFTER_SX = {'&::after': {display: 'none'}} as const;

// `android-wrapper-N` path — used when `data.length <= 6` (indices 0-5)
const androidConnectorSx = (index: number, isLast: boolean): SxProps<Theme> => {
    if (isLast) return HIDDEN_AFTER_SX;
    switch (index) {
        case 2:
            return {
                '&::after': {...connectorBaseAfterSx, ...bottomPatchSx},
                '@media (max-width: 992px)': {'&::after': ipadLeftFarDeltaSx},
                '@media (max-width: 768px)': {'&::after': tabHybridDeltaSx},
                '@media (max-width: 430px)': {'&::after': mobileBottomDeltaSx}
            };
        case 3:
            return {
                '&::after': {...connectorBaseAfterSx, ...leftNearPatchSx},
                '@media (max-width: 992px)': {'&::after': ipadBottomDeltaSx},
                '@media (max-width: 768px)': {'&::after': ipadBottomDeltaSx},
                '@media (max-width: 430px)': {'&::after': mobileBottomDeltaSx}
            };
        case 4:
            return {
                '&::after': {...connectorBaseAfterSx, ...leftNearPatchSx},
                '@media (max-width: 992px)': {'&::after': ipadRightOffset275DeltaSx},
                '@media (max-width: 768px)': {'&::after': tabRightOffset319DeltaSx},
                '@media (max-width: 430px)': {'&::after': mobileBottomDeltaSx}
            };
        case 5:
            // always the last rendered item when data.length<=6 — kept explicit for parity
            // with the source's dedicated `.android-wrapper-5::after{display:none}` rule.
            return HIDDEN_AFTER_SX;
        case 0:
        case 1:
        default:
            return {
                '&::after': connectorBaseAfterSx,
                ...(index === 1
                    ? {
                          '@media (max-width: 992px)': {'&::after': ipadBottomDeltaSx},
                          '@media (max-width: 768px)': {'&::after': ipadBottomDeltaSx}
                      }
                    : {}),
                '@media (max-width: 430px)': {'&::after': mobileBottomDeltaSx}
            };
    }
};

// `process-wrapper-N` path — used when `data.length > 6` (indices 0-6). Not exercised by
// any current production page (all live datasets have <=6 items); migrated for ownership
// completeness but not visually verifiable against a live route — flagged in the PR report.
const processConnectorSx = (index: number, isLast: boolean): SxProps<Theme> => {
    if (isLast) return HIDDEN_AFTER_SX;
    switch (index) {
        case 2:
            return {
                '&::after': {...connectorBaseAfterSx, ...bottomPatchSx},
                '@media (max-width: 992px)': {'&::after': ipadLeftFarDeltaSx}
            };
        case 5:
            return {'&::after': {...connectorBaseAfterSx, ...bottomPatchSx}};
        case 3:
            return {
                '&::after': {...connectorBaseAfterSx, ...leftNearPatchSx},
                '@media (max-width: 992px)': {'&::after': ipadBottomDeltaSx}
            };
        case 4:
            return {
                '&::after': {...connectorBaseAfterSx, ...leftNearPatchSx},
                '@media (max-width: 992px)': {'&::after': {...ipadRightOffset275DeltaSx, display: 'block !important'}}
            };
        case 6:
            return {
                '&::after': connectorBaseAfterSx,
                '@media (max-width: 992px)': {'&::after': ipadLeftNearDeltaSx}
            };
        // `.process-wrapper-1::after, .process-wrapper-3::after, .process-wrapper-7::after`
        // were grouped under the same ipadScreen (992px) rule in the deleted `_android.scss`
        // (see commit fda485b) — case 7 was dropped during migration. Restored here verbatim;
        // unreachable today since no live dataset exceeds 6 items, so this carries no visual risk.
        case 1:
        case 7:
            return {
                '&::after': connectorBaseAfterSx,
                '@media (max-width: 992px)': {'&::after': ipadBottomDeltaSx}
            };
        default:
            return {'&::after': connectorBaseAfterSx};
    }
};

// Migrated from globals.scss `.process-subheading` (+ nested `a` / `a:hover`). Sole owner
// confirmed — applied directly to this component's own subheading `Typography` below.
const processSubheadingSx = {
    '& a': {
        color: `${colors.text.inverse} !important`
    },
    '& a:hover': {
        textDecoration: 'underline !important'
    }
} as const;

const ProcessFollow = (props: any) => {
    const {data, heading, subheading, buttonLabel, btnClick} = props;
    return (
        <MUIContainer className="bottom-spacing  mobile-bottom-padding">
            <MUIBox className="spectrum-header-wrapper solutions">
                <Typography className="spectrum-heading color-white" component="h2">
                    {heading ? parse(heading) : ''}
                </Typography>

                <MUIBox className="divder-wrapper">
                    <hr className="divider-line" />
                </MUIBox>
                {subheading && (
                    <MUIBox className="sub-heading-box">
                        <Typography
                            className="spectrum-sub-heading color-white text-padding bottom-space process-subheading"
                            sx={processSubheadingSx}
                        >
                            {subheading ? parse(subheading) : ''}
                        </Typography>
                    </MUIBox>
                )}
            </MUIBox>
            <MUIGrid container gap={6} className="process-grid-parent" paddingBottom={2} sx={processGridParentSx}>
                {data?.map((item: any, index: number) => {
                    const isLast = index === data.length - 1;
                    const useAndroidSet = data.length <= 6;
                    const androidClass = useAndroidSet ? `android-wrapper-${index}` : '';
                    const processClass = useAndroidSet ? '' : `process-wrapper-${index}`;
                    const className = `android-wrapper ${androidClass} ${processClass}`;
                    const connectorSx = useAndroidSet ? androidConnectorSx(index, isLast) : processConnectorSx(index, isLast);
                    return (
                        <Fragment key={index + 1}>
                            <MUIGrid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={4}
                                className={className}
                                sx={[androidWrapperSx, connectorSx] as SxProps<Theme>}
                                order={{
                                    xs: item?.order?.xs ?? index + 1,
                                    sm: item?.order?.sm ?? index + 1,
                                    md: item?.order?.md ?? index + 1,
                                    lg: item?.order?.lg ?? index + 1
                                }}
                            >
                                <MUIBox className="android-heading color-white" sx={androidHeadingSx}>
                                    <MUITypography className="android-number" sx={androidNumberSx}>
                                        {index + 1}
                                    </MUITypography>
                                    <Typography className="android-heading-inner" sx={androidHeadingInnerSx}>
                                        {item?.heading ? parse(item.heading) : ''}
                                    </Typography>
                                </MUIBox>

                                <MUIBox className="android-icon" sx={androidIconSx}>
                                    <DisplayImage src={item.icon} alt={item.alt} priority={false} className="step-image" />
                                </MUIBox>
                                <MUIBox className="android-discription color-white" sx={androidDiscriptionSx}>
                                    <Typography
                                        className="description"
                                        sx={{
                                            fontFamily: `${fontFamily.body} !important`,
                                            fontWeight: '400 !important',
                                            fontSize: '16px !important',
                                            padding: '5px',
                                            lineHeight: '27px !important'
                                        }}
                                    >
                                        {item.discription ? parse(item.discription) : ''}
                                    </Typography>
                                </MUIBox>
                            </MUIGrid>
                        </Fragment>
                    );
                })}
            </MUIGrid>
            {buttonLabel && (
                <MUIBox className="spectrum-btn" sx={{textAlign: 'center'}}>
                    <MUIButton label={buttonLabel} aria-label={buttonLabel} sx={nftButtonSx} variant="contained" onClick={btnClick} />
                </MUIBox>
            )}
        </MUIContainer>
    );
};

export default memo(ProcessFollow);
